import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, FileCheck, FileSignature, UsersRound, ClipboardList, FlaskConical, Command } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Input } from "./ui/input";
import { api } from "../lib/api";

/**
 * Global search palette (⌘K / Ctrl+K). Doctor/admin only.
 * Indexes: patients, medical reports, prescriptions, certificates, interrogations.
 * Client-side fuzzy filter over pre-fetched lists (fast, no server changes).
 */
export default function GlobalSearch({ enabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState({ patients: [], reports: [], rx: [], certs: [], interros: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const nav = useNavigate();

  // Load lists lazily on first open
  const loadIndex = useCallback(async () => {
    if (idx.patients.length || idx.reports.length) return;
    try {
      const [pts, reps, rx, certs, its] = await Promise.all([
        api.get("/patients").catch(() => ({ data: [] })),
        api.get("/medical-reports").catch(() => ({ data: [] })),
        api.get("/prescriptions").catch(() => ({ data: [] })),
        api.get("/certificates").catch(() => ({ data: [] })),
        api.get("/interrogations").catch(() => ({ data: [] })),
      ]);
      setIdx({
        patients: pts.data || [],
        reports: reps.data || [],
        rx: rx.data || [],
        certs: certs.data || [],
        interros: its.data || [],
      });
    } catch { /* ignore */ }
  }, [idx.patients.length, idx.reports.length]);

  // ⌘K / Ctrl+K to toggle
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      const isCmd = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (isCmd) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  useEffect(() => {
    if (open) loadIndex();
    else { setQ(""); setActiveIdx(0); }
  }, [open, loadIndex]);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) {
      // Recent items by category (top 3 each)
      return [
        ...idx.patients.slice(0, 3).map((p) => ({
          kind: "patient", id: p.id, label: p.name, sub: p.email || p.dni || p.phone || "",
        })),
        ...idx.reports.slice(0, 3).map((r) => ({
          kind: "report", id: r.id, label: `${r.code} · ${r.patient_name}`, sub: (r.reason || "").slice(0, 80), patientId: r.patient_id,
        })),
      ];
    }
    const matches = (s) => (s || "").toString().toLowerCase().includes(query);
    const out = [];
    idx.patients.forEach((p) => {
      if (matches(p.name) || matches(p.email) || matches(p.dni) || matches(p.phone)) {
        out.push({ kind: "patient", id: p.id, label: p.name, sub: p.email || p.dni || p.phone || "" });
      }
    });
    idx.reports.forEach((r) => {
      if (matches(r.code) || matches(r.patient_name) || matches(r.reason) || matches(r.diagnosis_impression)) {
        out.push({ kind: "report", id: r.id, label: `${r.code} · ${r.patient_name}`, sub: (r.reason || "").slice(0, 80), patientId: r.patient_id });
      }
    });
    idx.rx.forEach((rx) => {
      if (matches(rx.code) || matches(rx.patient_name) || matches(rx.diagnosis)) {
        out.push({ kind: "rx", id: rx.id, label: `${rx.code} · ${rx.patient_name}`, sub: rx.diagnosis || "" });
      }
    });
    idx.certs.forEach((c) => {
      if (matches(c.code) || matches(c.patient_name) || matches(c.title)) {
        out.push({ kind: "cert", id: c.id, label: `${c.code} · ${c.patient_name}`, sub: c.title || c.cert_type || "" });
      }
    });
    idx.interros.forEach((i) => {
      if (matches(i.code) || matches(i.patient_name) || matches(i.chief_complaint)) {
        out.push({ kind: "interro", id: i.id, label: `${i.code} · ${i.patient_name}`, sub: (i.chief_complaint || "").slice(0, 80) });
      }
    });
    return out.slice(0, 20);
  }, [q, idx]);

  const go = (r) => {
    setOpen(false);
    switch (r.kind) {
      case "patient": nav("/app/historia-clinica", { state: { patientId: r.id } }); break;
      case "report": nav("/app/informes"); break;
      case "rx": nav("/app/prescriptions"); break;
      case "cert": nav("/app/certificados"); break;
      case "interro": nav("/app/interrogatorios"); break;
      default: break;
    }
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) go(results[activeIdx]);
    }
  };

  const iconFor = (kind) => {
    const map = { patient: UsersRound, report: FileCheck, rx: FileText, cert: FileSignature, interro: ClipboardList, exam: FlaskConical };
    return map[kind] || Search;
  };
  const labelFor = (kind) => ({ patient: "Paciente", report: "Informe", rx: "Receta", cert: "Certificado", interro: "Interrogatorio" }[kind] || "");

  if (!enabled) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
        <div className="border-b border-border px-4 py-3 flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Buscar pacientes, informes, recetas, certificados…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKey}
            data-testid="global-search-input"
            className="border-0 focus-visible:ring-0 shadow-none px-0 h-auto py-0 text-base"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
            <Command className="w-3 h-3" />K
          </kbd>
        </div>
        <div className="max-h-[380px] overflow-y-auto py-2" data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-sm text-muted-foreground text-center">
              {q ? "Sin coincidencias" : "Empieza a escribir…"}
            </div>
          ) : (
            results.map((r, i) => {
              const Icon = iconFor(r.kind);
              return (
                <button
                  key={`${r.kind}-${r.id}`}
                  onClick={() => go(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`gs-result-${r.kind}-${r.id}`}
                  className={`w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-accent transition-colors ${i === activeIdx ? "bg-accent" : ""}`}
                >
                  <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{r.label}</div>
                    {r.sub && <div className="text-xs text-muted-foreground truncate">{r.sub}</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {labelFor(r.kind)}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>↑ ↓ para navegar · ↵ para abrir · esc para cerrar</span>
          <span>{results.length} resultado{results.length !== 1 ? "s" : ""}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
